import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { Subscription, forkJoin, Observable, takeWhile } from 'rxjs';
import { FormGroup, FormControl } from '@angular/forms';
import { ILocalizationRecord } from '../shared/models/localization-record.model';
import { ApplicationModule } from '../shared/models/application-modules.model';
import { Language } from '../shared/models/language.model';
import { LocalizationService } from '../shared/services/localization.service';
import { Router } from '@angular/router';
import { ILocalizationGridResponse } from '../shared/models/localization-grid-response.dto';
import { ILocalizationGridRequestModel } from '../shared/models/localization-grid-request.model';
import { SortInfo } from 'src/app/shared/models/queryparams/sortinfo.model';
import { PageInfo } from 'src/app/shared/models/queryparams/pageinfo.model';
import { GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { SortDescriptor } from '@progress/kendo-data-query';
import { IDropdownSettings } from 'ng-multiselect-dropdown';
import { Store } from '@ngrx/store';
import { getACClientConfiguration, getACLicenseStatus, getLoggedInUserInfo, getLTLicenseStatus } from 'src/app/state/index';
import { AppState } from 'src/app/shared/models/store models/app-state.interface';
import { TranslationLoaderFacade } from '../translation-loader.facade';
import { TranslateService } from '@ngx-translate/core';
import { Modules } from '../shared/models/modules-enum';
import { ACClientConfiguration } from 'src/app/shared/models/acClientConfiguration';

@Component({
    selector: 'app-admin-localization-grid',
    templateUrl: './localization-grid.component.html',
    styleUrls: ['./localization-grid.component.scss']
})
export class LocalizationGridComponent implements OnInit, OnDestroy {
    isACLicenseEnabled$: Observable<boolean>;
    isLTLicenseEnabled$: Observable<boolean>;
    acClientConfiguration: ACClientConfiguration | null = null;
    mounted:boolean = true;
    public hasLTLicense: boolean = false;
    public hasACLicense: boolean = false;
    public applicationModules: ApplicationModule[] = [];
    public applicationsDefaultItem: ApplicationModule | undefined;
    public languagesDefaultItem: Language | undefined;
    public languageColumnTitle: string = '';
    public languages: Language[] = [];
    public showClearButton: boolean = false;
    public selectedApplications: ApplicationModule[] = [];

    public filterGroup: FormGroup = new FormGroup({
        applications: new FormControl(),
        languages: new FormControl(),
        filterText: new FormControl('')
    });
    public pageInfo: PageInfo = {
        PageSize: 0,
        Page: 0
    };
    public sortInfo: SortInfo = {
        SortColumn: '',
        sortDirection: null
    };
    public sort: SortDescriptor[] = [];
    public columnTitleText: string = '';
    public englishText: string = '';
    public spanishText: string = '';

    public dropdownSettings: IDropdownSettings = {
        singleSelection: false,
        idField: 'id',
        textField: 'name',
        selectAllText: this.translateService.instant('_General_Select_All_Text_'),
        itemsShowLimit: 3,
        unSelectAllText: this.translateService.instant('_General_Unselect_All_Ts_Text_'),
        enableCheckAll: false
    };
    public data: GridDataResult = {
        total: 0,
        data: [],
    };
    private subscriptions = new Subscription();
    private filterTextDefaultValue: string = '';

    private filteredModuleIds : {
        ACOnlyLicense: number[]; 
        LTOnlyLicense: number[];
        ACAndLTLicence: number[];
    } = {
        ACOnlyLicense:[
            Modules._UkgBankingConnections_, 
            Modules._AppointmentWidget_, 
            Modules._UkgAppointments_, 
            Modules._BankingEventsWidget_, 
            Modules._RecruitingEventsWidget_], 
        LTOnlyLicense:[
            Modules._UkgBankingConnections_, 
            Modules._Kiosk_], 
        ACAndLTLicence:[
            Modules._UkgBankingConnections_, 
            Modules._AppointmentWidget_, 
            Modules._Kiosk_, 
            Modules._RemoteCheckIn_, 
            Modules._UkgAppointments_, 
            Modules._BankingEventsWidget_, 
            Modules._RecruitingEventsWidget_]
    }

    private sortOrder: any=[Modules._UkgBankingConnections_, Modules._Kiosk_, Modules._RemoteCheckIn_, Modules._UkgAppointments_, Modules._AppointmentWidget_, Modules._BankingEventsWidget_, Modules._RecruitingEventsWidget_, Modules._WaitSmarter_];

    //moduleId mapping to localizationTextKeys
    private localizationKeys : Record<number, string> =  {    
        1: '_BankingConnectionsWebPortal_',
        2: '_AppointmentsWidget_',
        3: '_WaitSmarter_',
        4: '_Kiosk_',
        5: '_Remote_CheckIn_Web_Page_',
        6: '_FMSIAppointments_',
        7: '_BankingEventsWidget_',
        8: '_RecruitingEventsWidget_'
      };

    constructor(private localizationService: LocalizationService,
        private router: Router,
        private store: Store<AppState>,
        public translationLoaderFacade: TranslationLoaderFacade,
        private translateService: TranslateService) {
            this.isACLicenseEnabled$ = new Observable<boolean>();
            this.isLTLicenseEnabled$ = new Observable<boolean>();
        }

    ngOnInit(): void {
        this.translationLoaderFacade.fetchTranslations(); 
        this.englishText = this.translateService.instant('_General_English_EN_Text_');
        this.spanishText = this.translateService.instant('_General_Spanish_ES_Text_');
        
        this.isLTLicenseEnabled$ = this.store.select(getLTLicenseStatus);
        this.isACLicenseEnabled$ = this.store.select(getACLicenseStatus);

        this.subscriptions.add(this.isLTLicenseEnabled$.subscribe((data: boolean) => {
            this.hasLTLicense = data;

        }));
        this.subscriptions.add(this.isACLicenseEnabled$.subscribe((data: boolean) => {
            this.hasACLicense = data;

        }));
        this.subscriptions.add(
            this.filterGroup.controls.applications.valueChanges.subscribe({ next: app => this._onApplicationChange(app) })
        );
        this.subscriptions.add(
            this.filterGroup.controls.languages.valueChanges.subscribe({ next: lan => this._onLanguageChange(lan) })
        );
        this.subscriptions.add(
            this.filterGroup.controls.filterText.valueChanges.subscribe({ next: val => this._onFilterTextChanges(val) })
        );
        this._LoadDataAndAssignDefaults();

            this.store.select(getACClientConfiguration)
            .pipe(takeWhile(() => this.mounted))
            .subscribe(ecc => this.acClientConfiguration = ecc);
        
    }

    /*
    *   Load the grid data, languages, modules and assign the values previously selected if any
    *   Otherwise assign the default values
    */
    private _LoadDataAndAssignDefaults() {
        this.pageInfo = this.localizationService.getPageInfo() ?? { PageSize: 25, Page: 1 };
        this.sortInfo = this.localizationService.getSortInfo() ?? { sortDirection: 1, SortColumn: 'applicationModuleName' };

        this.subscriptions.add(
            forkJoin([
                this.localizationService.getLanguages(),
                this.localizationService.getApplicationModules()
            ]).subscribe({
                next: ([langs, modules]) => {
                        this.languages = langs;
                        this.languages.forEach(lang =>{
                            if(lang.languageId == 1){
                                lang.languageName = this.englishText;
                            }
                            else if(lang.languageId == 3){
                                lang.languageName = this.spanishText;
                            }
                          })
                      
                    this._selectDefaultLanguage();
                    this._updateTextValueColumnName();

                    this.applicationModules = modules;
                    this.applicationModules = this._customSortApplicationModules(modules);

                    //localization for modules Names in the dropdown
                     this.applicationModules.forEach(module => {
                        if (this.localizationKeys.hasOwnProperty(module.id)) {
                            module.name = this.translateService.instant(this.localizationKeys[module.id]);
                          }
                       })

                    this.filterModulesAccordingToLicensing(this.applicationModules);
                    this._selectDefaultModule();

                    if (this.languagesDefaultItem == undefined) {
                        throw Error("this.languagesDefaultItem was undefined! Which was not expected!")
                    }
                    this.filterTextDefaultValue = this.localizationService.getCurrentFilterTextValue() ?? '';
                    this.filterGroup.controls.filterText.setValue(this.filterTextDefaultValue);
                    this._loadGridDataFromService(
                        this.languagesDefaultItem.languageId,
                        this.applicationsDefaultItem?.id ? [this.applicationsDefaultItem?.id] : [1],
                        this.filterTextDefaultValue );   
                }
            }));
    }

private filterModulesAccordingToLicensing(modules: ApplicationModule[]){
    this.applicationModules = this.hasLTLicense && this.hasACLicense ?
    modules.filter(m => this.filteredModuleIds['ACAndLTLicence'].includes(m.id)) :
    this.hasACLicense ?
        modules.filter(m => this.filteredModuleIds['ACOnlyLicense'].includes(m.id)) :
        this.hasLTLicense ?
            modules.filter(m => this.filteredModuleIds['LTOnlyLicense'].includes(m.id)) :
            [];
    this.filterModulesAccordingToAcEvent();
}
    private filterModulesAccordingToAcEvent() {
        if (this.hasACLicense && !this.acClientConfiguration?.eventsEnabled) {
            this.applicationModules = this.applicationModules.filter(x => x.id != 7 && x.id != 8);
        }
}

  private _customSortApplicationModules(modules: ApplicationModule[]){
    const customOrder = this.sortOrder;
    return modules.sort((a, b) => {
        const indexA = customOrder.indexOf(a.id);
        const indexB = customOrder.indexOf(b.id);
        
        return indexA - indexB;
      });
    }

    /*
    *   Selects the default Application Module from the service if any previously made selection available
    *   Otherwise sets the first applicatin module which is **Admin Portal**
    */
    private _selectDefaultModule() {
        const defaultApps = [];
        this.applicationsDefaultItem = this.applicationModules.find(m => m.id == Modules._UkgBankingConnections_);
        if(this.applicationsDefaultItem)
            this.selectedApplications = [this.applicationsDefaultItem];
        this.filterGroup.controls.applications.setValue([this.applicationsDefaultItem]);
    }

    /*
    *   Selects the default language from the service if any previously made selection available
    *   Otherwise sets the first language which is **English**
    */
    private _selectDefaultLanguage() {
        this.languagesDefaultItem = this.languages.find(
            // If there is a currently selected language in the service use that language otherwise use english
            // Id of the language english is 1
            // if both of the above cases fail select the first language in the list
            lan => lan.languageId === (this.localizationService.getCurrentlySelectedLangauge()?.languageId ?? 1))
            ?? this.languages?.[0];
        this.filterGroup.controls.languages.setValue(this.languagesDefaultItem);
    }

    /**
     *  Load the grid data from the server with
     *  @param languageId Id for the language to fetch data for
     *  @param moduleIds Ids of the application modules a number array
     *  @param filterText Search text for the grid
     *  This method will use the sortInfo and page info internally
     */
    private _loadGridDataFromService(languageId: number, moduleIds: number[], filterText: string) {
        const request: ILocalizationGridRequestModel = {
            languageId, moduleIds, filterText,
            sortInfo: this.sortInfo,
            pageInfo: this.pageInfo
        };

        this.localizationService.getLocalizationGrid(request)
            .subscribe((response: ILocalizationGridResponse) => {
                response.data.forEach(module => {
                    if(module.applicationModuleId == 1){
                        module.applicationModuleName = this.translateService.instant('_BankingConnectionsWebPortal_');
                    }
                    else if(module.applicationModuleId == 4){
                        module.applicationModuleName = this.translateService.instant('_Kiosk_');
                    }
                    else if(module.applicationModuleId == 5){
                        module.applicationModuleName = this.translateService.instant('_RemoteCheckIn_');
                    }
                })
                this.data = {
                    total: response.totalRowCount,
                    data: response.data,
                };
                this.sort = [{
                    field: this.sortInfo.SortColumn,
                    dir: this.sortInfo.sortDirection === 1 ? 'asc' : 'desc'
                }];

                this.localizationService.setSortInfo(this.sortInfo);
                this.localizationService.setPageInfo(this.pageInfo);
                this._updateTextValueColumnName();
            });
    }

    /**
     *  Updates the grid for the latest filters applied from the server
     */
    private _refreshDataWithLatestFilters() {
        const languageId = this.filterGroup.controls.languages.value?.languageId;
        const moduleIds: number[] = this.filterGroup.controls.applications.value?.map((v: { id: number; name: string}) => v.id);
        const searchText = this.filterGroup.controls.filterText.value;
        if (languageId && moduleIds.length > 0) {
            this._loadGridDataFromService(languageId, moduleIds, searchText);
        } else {
            this.data = {
                total: 0,
                data: [],
            };
        }
    }

    /**
     *  Set the record selected for editing and move to edit view
     */
    private _selectRecordForEditingAndMoveToEditView(rowModelSelected: ILocalizationRecord) {
        this._selectRecordForEditing(rowModelSelected);
        this._moveToLocalizationSettings();
    }

    /**
     *  Set the record selected for editing
     */
    private _selectRecordForEditing(rowModelSelected: ILocalizationRecord) {
        this.localizationService.setLocalizationRecordToEdit(rowModelSelected);
    }

    /**
     *  Update the header text on language selection change
     */
    private _onLanguageChange(lan: Language) {
        if (lan) {
            this.localizationService.setCurrentlySelectedLanguage(lan);
        }
    }
    /**
     *  Update the header text on application selection change
     */
    private _onApplicationChange(app: ApplicationModule) {
        if (app) {
            this.localizationService.setSelectedApplication(app);
        }
    }

    /**
     *  When module selection changes update the service
     */
    private _onModuleChanges(app: ApplicationModule) {
        if (app) {
            this.localizationService.setSelectedApplication(app);
        } else {
            this._selectDefaultModule();
        }
    }

    /**
     *  When filter text changes update the service wiht latest value
     */
    private _onFilterTextChanges(val: string) {
        this._updateSearchTextClearButtonVisibility(val);
        this.localizationService.setCurrentFilterTextValue(val);
    }

    private _updateTextValueColumnName() {
        this.languageColumnTitle = this.filterGroup.controls.languages.value?.languageName + ' ' + this.translateService.instant('_Localization_Text_Ts_Text_');
    }

    private _updateSearchTextClearButtonVisibility(searchText: string) {
        if (searchText && searchText !== '') {
            this.showClearButton = true;
        } else {
            this.showClearButton = false;
        }
    }

    // -- Handlers
    public onEditClick(clickedRowItem: ILocalizationRecord) {
        this._selectRecordForEditingAndMoveToEditView(clickedRowItem);
    }

    public onApplicationChange() {
        this.pageInfo.Page = 1;
        this.selectedApplications = this.filterGroup.controls.applications.value;
        this._refreshDataWithLatestFilters();
    }

    public onLanguageChange() {
        this.pageInfo.Page = 1;
        this._refreshDataWithLatestFilters();
    }

    public onDropDownClose() {
        if (this.filterGroup.controls.applications.value
            && this.localizationService.getSelectedApplication() != this.filterGroup.controls.applications.value) {
            // this.localizationService.setSelectedApplications(this.filterGroup.controls.modules.value)
            this._onModuleChanges(this.filterGroup.controls.applications.value);
            this.pageInfo.Page = 1;
            this._refreshDataWithLatestFilters();
        }

    }

    public onFilterTextSearchClicked() {
        this.filterTextUpdated();
    }

    public onFilterTextEnterKeyPress() {
        this.filterTextUpdated();
    }

    public filterTextUpdated() {
        this.pageInfo.Page = 1;
        this._refreshDataWithLatestFilters();
    }

    public isLastSelectedModule() {
        return this.filterGroup.controls.applications.value.length === 1;
    }


    public onClearSearchTextClicked() {
        this.filterGroup.controls.filterText.setValue('');
        this._refreshDataWithLatestFilters();
    }

    public onPageChange(event: PageChangeEvent) {
        this.pageInfo.Page = (event.skip / this.pageInfo.PageSize) + 1;
        this._refreshDataWithLatestFilters();
    }

    public sortChange(event: SortDescriptor[]) {
        this.sortInfo = {
            SortColumn: event[0]?.field,
            sortDirection: event[0]?.dir === 'asc' ? 1 : 2
        };
        this._refreshDataWithLatestFilters();
    }
    // -- Handlers END

    // Navigation
    private _moveToLocalizationSettings() {
        this.router.navigate(['/admin/localization/edit']);
    }
    // -- NavigationEnd

    public languageTrackBy(index: number, item: Language) {
        return item.languageId;
    }

    ngOnDestroy(): void {

        if (this.router.routerState.snapshot.url.indexOf('localization') === -1) {
            this.localizationService.resetData();
        }
        this.subscriptions.unsubscribe();
    }
}
